/*
===============================================================================================================
                                 Copyright 2021-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable no-console */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { useRouter } from 'next/router';
import App from 'next/app';
import '@/utilities/i18n';
import AuthContextProvider from '@/contexts/auth-context';
import AdminContextProvider from '@/contexts/admin-context';
import LuminatePortalConfigProvider from '@/contexts/luminate-portal-config-context';
import MerchantContextProvider from '@/contexts/merchant-context';
import LocaleContextProvider from '@/contexts/locale-context';
import ThemeContextProvider from '@/contexts/theme-context';
import ChangesContextProvider from '@/contexts/changes-context';
import ValidationContextProvider from '@/contexts/validation-context';
import ValidateAddressContextProvider from '@/contexts/validate-address-context';
import RoutingContextProvider from '@/contexts/routing-context';
import TelemetryContextProvider from '@/contexts/telemetry-context';
import FullNavigation from '@/components/full-navigation';
import { getAppPathname, isStaticBuildEnabled } from '@/utilities/static-build';
import LoadingScreen from '@/components/loading-screen';
import { appFontsStyles } from '@/styles/appFonts';
import logging from '@/utilities/telemetry';
import ErrorBoundary from '@/components/error-boundary';
import IdleBannerLayout from '@/components/idle-banner-layout';
import E2EDemoContextProvider from '../src/E2E_DEMO/e2e-demo-context';
import '../src/utilities/lui-portal-utilities-manager';
import '../src/styles/globals.css';

// Suppress defaultProps warnings from React. TODO: https://doddleit.atlassian.net/browse/DEV-9181
const originalError = console.error;
console.error = (...args) => {
  if (!args[0]?.toString().includes('Support for defaultProps')) {
    originalError(...args);
  }
};

function ReturnsPortalAdmin({ Component, pageProps }) {
  const router = useRouter();

  // flags to handle the static build initial load
  const staticBuildLoaded = useRef(false);
  const [staticRouterUpdated, setStaticRouterUpdated] = useState(false);

  const onRouteChange = () => {
    if (staticBuildLoaded.current && !staticRouterUpdated) {
      setStaticRouterUpdated(true);
    }
  };

  /**
   * For a static-only build, the index.html page is served for all requests.
   * But Next.js router is server-based, so it can't read the current route in that case.
   * This effect updates the Next router path with what's in the actual URL, when in static mode.
   */
  useEffect(() => {
    if (pageProps.nonce) {
      // eslint-disable-next-line no-underscore-dangle
      window.__PORTAL_NONCE = pageProps.nonce;
    }

    router.events.on('routeChangeComplete', onRouteChange);

    if (isStaticBuildEnabled()) {
      // get the router path from a CDN url, stripping the base path first
      const appRouterPath = getAppPathname();

      // this triggers the router update with the correct router path, so that router.query becomes available in a static build
      router.push(appRouterPath + window.location.search);
      staticBuildLoaded.current = true;
    }
    return () => {
      router.events.off('routeChangeComplete', onRouteChange);
    };
  }, []);

  /**
   * In a static-only build need to wait for router to have updated before loading the app,
   * due to the usage of router.query within the app
   */
  if (isStaticBuildEnabled() && !staticRouterUpdated) {
    return <main suppressHydrationWarning><LoadingScreen /></main>;
  }

  if (staticRouterUpdated) {
    logging.trackPageView(router);
  }

  return (
    <>
      <Head>
        <title>Returns Portal Admin</title>
        <meta name="description" content="Returns Portal Admin" />
        <meta name="robots" content="noindex" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
        <meta name="theme-color" content="#000000" />
        <meta name="google" content="notranslate" />

        <link id="favicon" rel="icon shortcut" />
        <link id="apple-touch-icon" rel="apple-touch-icon" />
        <link id="favicon-16x16" rel="icon" type="image/x-icon" />
        <link id="favicon-32x32" rel="icon" type="image/x-icon" />
        <link id="android-chrome-192x192" rel="android-chrome" sizes="192x192" />
        <link id="android-chrome-512x512" rel="android-chrome" sizes="512x512" />
      </Head>

      <main>
        <ErrorBoundary>
          <LuminatePortalConfigProvider>
            <AdminContextProvider>
              <AuthContextProvider localConfig={pageProps.authData}>
                <ThemeContextProvider>
                  <MerchantContextProvider>
                    <TelemetryContextProvider>
                      <LocaleContextProvider>
                        <RoutingContextProvider>
                          <ValidateAddressContextProvider>
                            <E2EDemoContextProvider>
                              <ChangesContextProvider>
                                <ValidationContextProvider>
                                  <FullNavigation>
                                    <IdleBannerLayout>
                                      <Component {...pageProps} />
                                    </IdleBannerLayout>
                                  </FullNavigation>
                                </ValidationContextProvider>
                              </ChangesContextProvider>
                            </E2EDemoContextProvider>
                          </ValidateAddressContextProvider>
                        </RoutingContextProvider>
                      </LocaleContextProvider>
                    </TelemetryContextProvider>
                  </MerchantContextProvider>
                </ThemeContextProvider>
              </AuthContextProvider>
            </AdminContextProvider>
          </LuminatePortalConfigProvider>
        </ErrorBoundary>
      </main>
      <div id="absolute-positioned-content" />
      <style jsx global>
        {appFontsStyles}
      </style>
    </>
  );
}

ReturnsPortalAdmin.defaultProps = {
  Component: null,
  pageProps: {},
};

ReturnsPortalAdmin.propTypes = {
  Component: PropTypes.elementType,
  pageProps: PropTypes.shape({
    authData: PropTypes.shape({
      username: PropTypes.string,
      password: PropTypes.string,
      isAdmin: PropTypes.string,
      rootOrgId: PropTypes.string,
      isDevLogin: PropTypes.bool,
    }),
  }),
};

ReturnsPortalAdmin.getInitialProps = async (appContext) => {
  const appProps = await App.getInitialProps(appContext);
  const nonce = appContext.ctx.req?.headers['x-nonce'] || ''; // Make nonce available to the frontend
  const isDevelopment = process.env.NODE_ENV === 'development';
  if (isDevelopment) {
    appProps.pageProps.authData = {
      username: process.env.REACT_APP_USERNAME,
      password: process.env.REACT_APP_PASSWORD,
      isAdmin: process.env.REACT_APP_IS_ADMIN,
      isDevLogin: true,
    };
  }
  appProps.pageProps.nonce = nonce;
  return { ...appProps };
};

export default ReturnsPortalAdmin;
